// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import React from 'react';
import { Button, ContentLoader, Heading, Toolbar, ToolbarExpander, useTranslate } from '@plesk/ui-library';
import { useMutation, useQuery } from '@apollo/client/react';
import { redirect } from 'jsw';
import { addToast } from 'helpers/toasts';
import McpSettings from './McpSettings';
import COPILOT_MCP_SETTINGS from 'queries/CopilotMcpSettings.graphql';
import DEAUTHORIZE_COPILOT_MCP from 'queries/DeauthorizeCopilotMcp.graphql';

type SettingsProps = {
    onBack: () => void;
};

const Settings: React.FC<SettingsProps> = ({ onBack }) => {
    const translate = useTranslate('components.copilot');
    const { data } = useQuery(COPILOT_MCP_SETTINGS);
    const [deauthorizeMcp] = useMutation(DEAUTHORIZE_COPILOT_MCP);
    const mcp = data?.viewer.copilotMcp;

    const handleDeauthorizeMcp = () => deauthorizeMcp()
        .then(() => true)
        .catch(error => {
            addToast({ intent: 'danger', message: String(error) });
            return false;
        });

    return (
        <div className="plesk-copilot-settings">
            <div className="plesk-copilot-chat-header">
                <Toolbar>
                    <Button icon="arrow-left" ghost tooltip={translate('settings-view.back')} tooltipAsLabel onClick={onBack} />
                    <Heading level={2} className="plesk-copilot-settings-title">{translate('settings')}</Heading>
                    <ToolbarExpander />
                </Toolbar>
            </div>
            <div className="plesk-copilot-chat-messages">
                {mcp ? (
                    <McpSettings
                        isDashboardConnected={mcp.isDashboardConnected}
                        isMcpAuthorized={mcp.isMcpAuthorized}
                        isMcpAuthorizationAvailable={!!mcp.mcpAuthorizeUrl}
                        onConnectDashboard={() => redirect(mcp.dashboardConnectUrl)}
                        onDisconnectDashboard={async () => {
                            // The MCP key belongs to the dashboard connection, so drop it first
                            if (await handleDeauthorizeMcp()) {
                                redirect(mcp.dashboardDisconnectUrl);
                            }
                        }}
                        onAuthorizeMcp={() => redirect(mcp.mcpAuthorizeUrl)}
                        onDeauthorizeMcp={handleDeauthorizeMcp}
                    />
                ) : (
                    <ContentLoader />
                )}
            </div>
        </div>
    );
};

export default Settings;
